import {defineStore} from 'pinia';
import cleanGraphQLResponse from '~~/utils/clean-graphql';

type WishlistItem = any

type UserData = {
    email: string
    plainPassword: string
    passwordRepeat: string
    address: string
    firstName: string
    lastName: string
    company: string
    extraInfo: string
    street: string
    streetNo: string
    zip: string
    city: string
    country: string
    phone: string
    fax: string
    mobile: string
    vatId: string
}

type WishlistState = {
    wishlistItems: WishlistItem[]
    wishlistId: string | null
    initializing: boolean
    ready: boolean
    _initPromise: Promise<void> | null
    userData: UserData
}

export const useWishlistStore = defineStore('wishlist', {
    state: (): WishlistState => ({
        wishlistItems: [],
        wishlistId: null,
        initializing: false,
        ready: false,
        _initPromise: null,

        userData: {
            email: '',
            plainPassword: '',
            passwordRepeat: '',
            address: 'mr',
            firstName: '',
            lastName: '',
            company: '',
            extraInfo: '',
            street: '',
            streetNo: '',
            zip: '',
            city: '',
            country: '',
            phone: '',
            fax: '',
            mobile: '',
            vatId: '',
        },
    }),

    getters: {
        items: (state): WishlistItem[] => state.wishlistItems,
        itemCount: (state) => state.wishlistItems.length,
        isReadyState: (state): boolean => state.ready,
    },

    actions: {
        /**
         * Call once on client (e.g. app.vue onMounted or first usage).
         */
        async init(): Promise<void> {
            if (import.meta.server) return;
            if (this.ready) return;
            if (this._initPromise) return this._initPromise;

            this._initPromise = (async () => {
                if (this.initializing) return;
                this.initializing = true;

                if (!this.wishlistId) {
                    await this.initWishlist();
                }

                this.ready = true;
                this.initializing = false;
            })();

            return this._initPromise;
        },

        async ensureReady(): Promise<void> {
            await this.init();
        },

        async initWishlist(): Promise<void> {
            if (import.meta.server) return;

            const gql = useGql();
            const wishlistIdCookie = useCookie<string | null>('wishlistId');

            this.wishlistId = wishlistIdCookie.value;

            // 1) falls kein Cookie: Wishlist erstellen
            if (!this.wishlistId) {
                const resCreate = await gql<any>('createWishlist');

                const created = cleanGraphQLResponse(resCreate?.createWishlist?.wishlist);
                this.wishlistId = created?.id ?? null;
                wishlistIdCookie.value = this.wishlistId;
            }

            if (!this.wishlistId) {
                this.wishlistItems = [];
                return;
            }

            // 2) Wishlist laden
            const resFind = await gql<any>('findWishlist', {id: this.wishlistId});

            const {wishlist} = cleanGraphQLResponse(resFind) ?? {};
            this.wishlistItems = wishlist?.items ?? [];
        },

        async add(article: any): Promise<void> {
            await this.ensureReady();
            if (!this.wishlistId) return;

            const gql = useGql();
            const toast = useToast();

            const existing = this.wishlistItems.find((wi: any) => wi?.article?.id === article?.id);

            if (!existing) {
                const res = await gql<any>('createItem', {
                    input: {
                        amount: 1,
                        article: article.id,
                        wishlist: this.wishlistId,
                    },
                });

                const item = cleanGraphQLResponse(res?.createItem?.item);
                if (item) this.wishlistItems.push(item);
            } else {
                existing.amount = (existing.amount ?? 0) + 1;

                await gql<any>('updateItem', {
                    input: {
                        id: existing.id,
                        amount: existing.amount,
                    },
                });
            }

            toast.add({
                title: 'Merkzettel',
                description: 'Artikel zum Merkzettel hinzugefügt.',
                color: 'success',
            });
        },

        async remove(article: any): Promise<void> {
            await this.ensureReady();
            const found = this.wishlistItems.find((wi: any) => wi?.article?.id === article?.id);
            if (found) await this.delete(found);
        },

        async update(item: any): Promise<void> {
            await this.ensureReady();

            const gql = useGql();
            const found = this.wishlistItems.find((wi: any) => wi?.article?.id === item?.article?.id);
            if (!found) return;

            await gql<any>('updateItem', {
                input: {
                    id: found.id,
                    amount: found.amount,
                },
            });
        },

        async delete(item: any): Promise<void> {
            await this.ensureReady();

            const gql = useGql();
            const toast = useToast();

            const idx = this.wishlistItems.findIndex((wi: any) => wi?._id === item?._id);
            if (idx >= 0) this.wishlistItems.splice(idx, 1);

            await gql<any>('deleteItem', {
                input: {id: item.id},
            });

            toast.add({
                title: 'Merkzettel',
                description: 'Artikel vom Merkzettel entfernt.',
                color: 'success',
            });
        },

        has(article: any): boolean {
            return this.wishlistItems.some((wi: any) => String(wi?.article?._id) === String(article._id));
        },

        async takeOwnerShipOfWishlist(user: any): Promise<void> {
            await this.ensureReady();
            if (!this.wishlistId) return;

            const gql = useGql();

            await gql<any>('updateWishlist', {
                input: {
                    id: this.wishlistId,
                    user: user.id,
                },
            });
        },

        async login(user: any): Promise<void> {
            await this.ensureReady();

            const gql = useGql();

            this.userData = {
                ...user.userData,
                street: `${user.userData.street ?? ''} ${user.userData.streetNo ?? ''}`.trim(),
                phone: user.userData.phone ?? user.userData.mobile,
            };

            const wishlistsRes = await gql<any>('findWishlists', {user: user.id});
            const wishlists = cleanGraphQLResponse(wishlistsRes)?.wishlists ?? [];

            if (!wishlists.length) {
                await this.takeOwnerShipOfWishlist(user);
                return;
            }

            const first = wishlists[0];

            const wishlistRes = await gql<any>('findWishlist', {id: first.id});
            const wishlist = cleanGraphQLResponse(wishlistRes?.wishlist);

            if (wishlist?.items?.length) {
                this.wishlistItems = wishlist.items;
                this.wishlistId = wishlist.id;
                useCookie<string | null>('wishlistId').value = this.wishlistId;
            } else {
                if (wishlist?.id && wishlist.id !== this.wishlistId) {
                    await gql<any>('deleteWishlist', {input: {id: wishlist.id}});
                }
                await this.takeOwnerShipOfWishlist(user);
            }
        },

        async logout(): Promise<void> {
            if (import.meta.server) return;

            useCookie<string | null>('wishlistId').value = null;
            this.wishlistItems = [];
            this.wishlistId = null;
            this.ready = false;
            this._initPromise = null;

            await this.init();
        },
    },
});
